import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../Card';
import { TierBudgetSnapshot } from '../../types/tier1Portfolio';
import { previewDeployableCapital } from '../../constants/tierPresets';
import DeployableCapitalBar from './DeployableCapitalBar';
import TierBadge from './TierBadge';
import { fmtUsd } from './format';

interface Props {
  navUsd: number;
  maxUtilization: number;
  tier1Enabled: boolean;
  tier6Enabled: boolean;
  tier1Alloc: number;
  tier6Alloc: number;
  budgets: TierBudgetSnapshot[];
  tier1Id?: string;
  tier6Id?: string;
  onOpenTier1: () => void;
  onOpenTier6: () => void;
}

const AccountPortfolioOverviewCard: React.FC<Props> = ({
  navUsd,
  maxUtilization,
  tier1Enabled,
  tier6Enabled,
  tier1Alloc,
  tier6Alloc,
  budgets,
  tier1Id,
  tier6Id,
  onOpenTier1,
  onOpenTier6,
}) => {
  const preview = previewDeployableCapital(navUsd, maxUtilization, [
    { enabled: tier1Enabled, allocationPct: tier1Alloc },
    { enabled: tier6Enabled, allocationPct: tier6Alloc },
  ]);

  const b1 = budgets.find((b) => b.tierId === tier1Id);
  const b6 = budgets.find((b) => b.tierId === tier6Id);

  const segments = [];
  if (tier1Enabled && b1) {
    segments.push({
      key: 't1',
      label: 'Tier 1 deployed',
      usd: b1.tierDeployedUsd,
      className: 'bg-sky-500',
    });
  }
  if (tier6Enabled && b6) {
    segments.push({
      key: 't6',
      label: 'Tier 6 deployed',
      usd: b6.tierDeployedUsd,
      className: 'bg-violet-500',
    });
  }
  const deployedSum = segments.reduce((s, x) => s + x.usd, 0);
  const deployableLeft = Math.max(0, preview.deployableUsd - deployedSum);
  if (deployableLeft > 0) {
    segments.push({
      key: 'avail',
      label: 'Deployable headroom',
      usd: deployableLeft,
      className: 'bg-slate-300',
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Two-slot portfolio</CardTitle>
        <p className="text-sm text-gray-500">
          Tier views live on the tabs above — not on the home Dashboard page.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <DeployableCapitalBar
          navUsd={navUsd}
          deployableUsd={preview.deployableUsd}
          cashReserveUsd={preview.cashReserveUsd}
          segments={segments}
        />
        <div className="grid gap-3 md:grid-cols-2">
          {tier1Enabled && b1 ? (
            <div className="rounded-lg border border-sky-100 bg-sky-50/50 p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <TierBadge kind="tier1" label="Long-term (Tier 1)" />
                <button
                  type="button"
                  onClick={onOpenTier1}
                  className="text-xs font-medium text-primary-600 hover:underline"
                >
                  Open tab →
                </button>
              </div>
              <p className="text-sm text-gray-700">
                {fmtUsd(b1.tierDeployedUsd)} / {fmtUsd(b1.tierBudgetUsd)} deployed
              </p>
              <p className="text-xs text-gray-500">{fmtUsd(b1.tierAvailableUsd)} room</p>
            </div>
          ) : null}
          {tier6Enabled && b6 ? (
            <div className="rounded-lg border border-violet-100 bg-violet-50/50 p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <TierBadge kind="tier6" label="Active options (Tier 6)" />
                <button
                  type="button"
                  onClick={onOpenTier6}
                  className="text-xs font-medium text-primary-600 hover:underline"
                >
                  Open tab →
                </button>
              </div>
              <p className="text-sm text-gray-700">
                {fmtUsd(b6.tierDeployedUsd)} / {fmtUsd(b6.tierBudgetUsd)} deployed
              </p>
              <p className="text-xs text-gray-500">{fmtUsd(b6.tierAvailableUsd)} room</p>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

export default AccountPortfolioOverviewCard;
